import {
  useState, useEffect, useCallback, useRef,
} from 'react';

const WORKER_URL = process.env.REACT_APP_WORKER_URL || 'wss://volleyball-scoreboard.pandlr.workers.dev';

export function useRemoteScore() {
  const [remoteScore, setRemoteScore] = useState({ teamA: 0, teamB: 0 });
  const [remoteState, setRemoteState] = useState(null); // snapshot do app (players/teams/config)
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const ws = new WebSocket(WORKER_URL);

      ws.onopen = () => {
        setIsConnected(true);
        setError(null);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'score') {
            setRemoteScore(message.data);
          } else if (message.type === 'state') {
            setRemoteState(message.data);
          }
        } catch (err) {
          console.error('Error parsing message:', err);
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        wsRef.current = null;
        reconnectTimeoutRef.current = setTimeout(connect, 3000);
      };

      ws.onerror = () => {
        setError('Erro de conexão');
        setIsConnected(false);
      };

      wsRef.current = ws;
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const sendAction = useCallback((action, team) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action, team }));
    }
  }, []);

  const addPoint = useCallback((team) => {
    sendAction('add', team);
  }, [sendAction]);

  const removePoint = useCallback((team) => {
    sendAction('remove', team);
  }, [sendAction]);

  const resetScore = useCallback(() => {
    sendAction('reset');
  }, [sendAction]);

  const setScore = useCallback((teamA, teamB) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'set', teamA, teamB }));
    }
  }, []);

  // Envia um comando para o app aplicar (ex.: criar jogador, check-in, montar time).
  const sendCommand = useCallback((command) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'command', data: command }));
    }
  }, []);

  useEffect(() => () => {
    disconnect();
  }, [disconnect]);

  return {
    remoteScore,
    remoteState,
    isConnected,
    error,
    connect,
    disconnect,
    addPoint,
    removePoint,
    resetScore,
    setScore,
    sendCommand,
  };
}
